/**
 * In-flight request deduplication — Round P125 (Feb 2026).
 *
 * The portfolio page had 23 duplicate `/api/portfolio/summary` calls
 * per load: PortfolioSummary, CapitalTrackerCard, BenchmarkComparison,
 * HistoricalEarningsCard, MyPortfolioTab, plus React strict-mode
 * double-invokes... all firing the same URL at basically the same
 * time. They queued through the browser's 6-concurrent HTTP cap and
 * starved un-duped endpoints from running.
 *
 * `dedupedGet(url, config)` behaves exactly like `axios.get`, but if
 * an identical request (same URL + same query params + same
 * withCredentials setting) is already in flight, it returns the
 * SAME promise instead of firing a second HTTP hop. The dedup key
 * is torn down when the promise settles, so subsequent calls (e.g.
 * after a refetch tick) go through normally.
 *
 * Scope: strictly for READ-only GETs. NEVER dedupe POST / PATCH /
 * DELETE — those have side effects and must fire once per call.
 */
import axios from 'axios';

const inflight = new Map();

function keyFor(url, config) {
  const params = config?.params || {};
  const withCreds = config?.withCredentials === true;
  const paramKey = Object.keys(params)
    .sort()
    .map((k) => `${k}=${params[k] ?? ''}`)
    .join('&');
  return `${withCreds ? 'C' : 'N'}|${url}|${paramKey}`;
}

export function dedupedGet(url, config) {
  const key = keyFor(url, config);
  const warm = recent.get(key);
  if (warm) {
    recent.delete(key);
    if (Date.now() < warm.until) return warm.promise;
  }
  const cached = inflight.get(key);
  if (cached) return cached;
  const p = axios.get(url, config).finally(() => {
    inflight.delete(key);
  });
  inflight.set(key, p);
  return p;
}

// Sep 2026 — fire a GET early (e.g. right after /auth/me) and let the first
// `dedupedGet` for the same URL+params within `ttlMs` reuse the result.
const recent = new Map();
export function prefetchGet(url, config, ttlMs = 8000) {
  const key = keyFor(url, config);
  const promise = dedupedGet(url, config);
  recent.set(key, { promise, until: Date.now() + ttlMs });
  promise.catch(() => recent.delete(key));
  return promise;
}

// Sep 2026 — app-wide in-flight dedup for plain `axios.get` calls too. The
// portfolio page fired ~165 requests per load with the same URL+params
// repeated 5-10× (income-goal, paycheck, contrast, benchmark, heatmap…),
// all queueing in the browser's 6-connection lane in front of the headline.
// Wraps the default adapter: an identical GET already in flight shares the
// same response promise. SWR re-fetches (x-swr-refetch) are never deduped.
let adapterInstalled = false;
// Sep 2026 — boot gate: on the portfolio view, every other GET waits until the
// live headline has landed (or 1.5 s, whichever first) so the top card's
// request is alone on the wire AND alone on the backend event loop.
const GATE_FREE = ['/auth/', '/portfolios', '/portfolio/headline', '/prices/stream', '/version'];
let gateOpen = !/view=portfolio|^\/portfolio/.test(typeof window !== 'undefined' ? (window.location.search + window.location.pathname) : '');
let gateResolve = null;
const gate = gateOpen ? Promise.resolve() : new Promise((res) => { gateResolve = res; });
export function openBootGate() {
  if (gateOpen) return;
  gateOpen = true;
  gateResolve?.();
}
if (!gateOpen && typeof window !== 'undefined') setTimeout(openBootGate, 1500);

export function installGetDedup() {
  if (adapterInstalled) return;
  adapterInstalled = true;
  const base = axios.getAdapter(axios.defaults.adapter);
  const live = new Map();
  axios.defaults.adapter = async (config) => {
    const method = (config.method || 'get').toLowerCase();
    if (method !== 'get' || config.headers?.['x-swr-refetch'] || config.responseType === 'blob' || config.responseType === 'arraybuffer') {
      return base(config);
    }
    const url = String(config.url || '');
    if (!gateOpen && !GATE_FREE.some((s) => url.includes(s))) await gate;
    const key = keyFor(config.url, config);
    const hit = live.get(key);
    if (hit) return hit.then((r) => ({ ...r, config }));
    const p = base(config).finally(() => {
      live.delete(key);
      if (url.includes('/portfolio/headline')) openBootGate();
    });
    live.set(key, p);
    return p;
  };
}
